import {
  ClusterOutlined,
  NodeIndexOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { assertNever } from "@p0security/shared/types/index";
import {
  AgenticRequestorRule,
  RequestorRule,
} from "@p0security/shared/types/workflow/types";
import { StyledRoutingRulePreview } from "components/Jit/Routing/styles";
import type { ReactNode } from "react";

import { IdpGroupsPreview, RequestorIdpGroupPreview } from "./IdpGroupPreview";

export type RequestorRulePreview =
  | RequestorRule
  | {
      type: "genericGroup";
    };

type Props = {
  requestor: RequestorRulePreview;
};

const agentSummary = (agent: AgenticRequestorRule["agent"] | undefined) => {
  if (!agent) return "an agent";
  switch (agent.type) {
    case "any":
      return "any agent";
    case "agent":
      return agent.azp ? `MCP client ${agent.azp}` : "an MCP client";
    case "agent-owner":
      return agent.owner ? `agent owned by ${agent.owner}` : "agent by owner";
    default:
      return "an agent";
  }
};

const agenticUserSummary = (
  user: AgenticRequestorRule["user"] | undefined
): ReactNode => {
  if (!user) return "an identity";
  switch (user.type) {
    case "any":
      return "any identity";
    case "user":
      return user.uid || "a specific user";
    case "group":
      return <IdpGroupsPreview idpGroups={user.groups} effect={user.effect} />;
    default:
      return "an identity";
  }
};

export const RequestorPreview: React.FC<Props> = ({ requestor }) => {
  if (requestor.type === "genericGroup") return <RequestorIdpGroupPreview />;
  switch (requestor.type) {
    case "any":
      return (
        <StyledRoutingRulePreview>
          <TeamOutlined />
          <span>Any identity</span>
        </StyledRoutingRulePreview>
      );
    case "user":
      return (
        <StyledRoutingRulePreview>
          <UserOutlined />
          <span>{requestor.uid || "Specific user"}</span>
        </StyledRoutingRulePreview>
      );
    case "group":
      return (
        <IdpGroupsPreview
          idpGroups={requestor.groups}
          effect={requestor.effect}
        />
      );
    case "agentic":
      return (
        <StyledRoutingRulePreview>
          <NodeIndexOutlined />
          <span>{agenticUserSummary(requestor.user)}</span>
          <UserOutlined />
          <span>via {agentSummary(requestor.agent)}</span>
        </StyledRoutingRulePreview>
      );
    case "wif":
      return (
        <StyledRoutingRulePreview>
          <ClusterOutlined />
          <span>
            {requestor.pool
              ? `Workload identity in ${requestor.pool}`
              : "Workload identity"}
          </span>
        </StyledRoutingRulePreview>
      );
    default:
      return assertNever(requestor);
  }
};
